import { columnIndexToLetter } from "./feishuSheetUrl";
import { extractFeishuImageFileToken, formatFeishuCellValue } from "./feishuCellValue";
import {
  filterRoleColumnMerges,
  findRoleColumnMergeAnchorRow,
  type FeishuMergeRange,
} from "./feishuSheetMerges";

/** B 列（0-based index 1）为剧本角色 */
export const SCRIPT_ROLE_COLUMN_INDEX = 1;

/** A 列不展示 */
export const SCRIPT_HIDDEN_COLUMN_INDEXES = new Set([0]);

/** 数据列最多展示到 D 列（E 列及以后不展示） */
export const SCRIPT_MAX_VISIBLE_COLUMN_INDEX = 3;

/** 气氛员角色前缀（如「气氛员」「气氛员A」均匹配） */
export const SCRIPT_ATMOSPHERE_ROLE_PREFIX = "气氛员";

/** 发单号角色（精确匹配） */
export const SCRIPT_DISPATCHER_ROLE = "发单号";

export function isScriptAtmosphereRole(role: string): boolean {
  return role.startsWith(SCRIPT_ATMOSPHERE_ROLE_PREFIX);
}

export function isScriptDispatcherRole(role: string): boolean {
  return role === SCRIPT_DISPATCHER_ROLE;
}

export function isScriptVisibleRole(role: string): boolean {
  return isScriptAtmosphereRole(role) || isScriptDispatcherRole(role);
}

export function isScriptImageUnderstandingRole(role: string): boolean {
  return isScriptAtmosphereRole(role);
}

export interface ParsedScriptSheetRow {
  rowIndex: number;
  rowNumber: number;
  role: string;
  /** B 列原始值是否为空（角色由上方行继承） */
  roleIsInherited: boolean;
  cells: Record<string, string>;
  /** 列字母 → fileToken，用于 img 渲染 */
  cellImages: Record<string, string>;
  roleImageFileToken?: string;
}

function trimCell(value: unknown): string {
  return formatFeishuCellValue(value).trim();
}

/** 获取除 A、B 外需要展示的数据列（C–D） */
export function getScriptSheetDataColumnLetters(): string[] {
  const letters: string[] = [];

  for (
    let colIndex = 2;
    colIndex <= SCRIPT_MAX_VISIBLE_COLUMN_INDEX;
    colIndex += 1
  ) {
    letters.push(columnIndexToLetter(colIndex + 1));
  }

  return letters;
}

function readRoleCell(input: {
  values: string[][];
  rawValues?: unknown[][];
  rowIndex: number;
}): {
  roleRaw: string;
  roleImageFileToken?: string;
} {
  const row = input.values[input.rowIndex] ?? [];
  const rawRow = input.rawValues?.[input.rowIndex] ?? [];
  const roleRawCell = rawRow[SCRIPT_ROLE_COLUMN_INDEX];
  const roleImageFileToken = extractFeishuImageFileToken(roleRawCell) ?? undefined;
  const roleRaw = roleImageFileToken ? "" : trimCell(row[SCRIPT_ROLE_COLUMN_INDEX]);

  return { roleRaw, roleImageFileToken };
}

/**
 * 解析飞书剧本表格：
 * - 隐藏 A 列
 * - B 列为角色；优先使用 API merges 识别合并块，否则空单元格向上继承
 * - 数据列仅展示 C–D（E 列及以后忽略）
 */
export function parseScriptSheetValues(
  values: string[][],
  rawValues?: unknown[][],
  roleColumnMerges: FeishuMergeRange[] = []
): ParsedScriptSheetRow[] {
  let currentRole = "";
  const bColumnMerges = filterRoleColumnMerges(roleColumnMerges);

  return values.map((row, rowIndex) => {
    const rawRow = rawValues?.[rowIndex] ?? [];
    const mergeAnchorRow = findRoleColumnMergeAnchorRow(bColumnMerges, rowIndex);
    const roleCell =
      mergeAnchorRow !== undefined
        ? readRoleCell({ values, rawValues, rowIndex: mergeAnchorRow })
        : readRoleCell({ values, rawValues, rowIndex });
    const { roleRaw, roleImageFileToken } = roleCell;
    const isMergeAnchorRow =
      mergeAnchorRow !== undefined && mergeAnchorRow === rowIndex;
    const inRoleColumnMerge = mergeAnchorRow !== undefined;
    const roleIsMergeInherited =
      mergeAnchorRow !== undefined && mergeAnchorRow !== rowIndex;
    const useMergeApi = bColumnMerges.length > 0;
    const roleIsMergedDuplicate =
      !useMergeApi &&
      !roleIsMergeInherited &&
      Boolean(roleRaw && currentRole && roleRaw === currentRole);
    const roleIsInherited =
      roleIsMergeInherited ||
      roleIsMergedDuplicate ||
      (!useMergeApi &&
        !roleRaw &&
        !roleImageFileToken &&
        Boolean(currentRole)) ||
      (useMergeApi &&
        !inRoleColumnMerge &&
        !isMergeAnchorRow &&
        !roleRaw &&
        !roleImageFileToken &&
        Boolean(currentRole));

    if (roleRaw && !roleIsMergeInherited && !roleIsMergedDuplicate) {
      currentRole = roleRaw;
    } else if (roleIsMergeInherited && roleRaw) {
      currentRole = roleRaw;
    }

    const cells: Record<string, string> = {};
    const cellImages: Record<string, string> = {};

    if (roleImageFileToken && mergeAnchorRow === rowIndex) {
      cellImages.B = roleImageFileToken;
    }

    for (let colIndex = 2; colIndex < row.length; colIndex += 1) {
      if (colIndex > SCRIPT_MAX_VISIBLE_COLUMN_INDEX) break;
      if (SCRIPT_HIDDEN_COLUMN_INDEXES.has(colIndex)) continue;
      if (colIndex === SCRIPT_ROLE_COLUMN_INDEX) continue;

      const letter = columnIndexToLetter(colIndex + 1);
      const fileToken = extractFeishuImageFileToken(rawRow[colIndex]);
      if (fileToken) {
        cellImages[letter] = fileToken;
        cells[letter] = "";
      } else {
        cells[letter] = trimCell(row[colIndex]);
      }
    }

    return {
      rowIndex,
      rowNumber: rowIndex + 1,
      role: roleRaw || currentRole,
      roleIsInherited,
      cells,
      cellImages,
      roleImageFileToken:
        !roleIsMergeInherited && roleImageFileToken ? roleImageFileToken : undefined,
    };
  });
}

export function hasScriptSheetContentInRow(row: ParsedScriptSheetRow): boolean {
  return Boolean((row.cells.C || "").trim() || row.cellImages.C);
}

export function filterScriptSheetRows(rows: ParsedScriptSheetRow[]): ParsedScriptSheetRow[] {
  return rows.filter(
    (row) => isScriptVisibleRole(row.role) && hasScriptSheetContentInRow(row)
  );
}

export interface ScriptSheetImageRef {
  fileToken: string;
  rowNumber: number;
  column: string;
  role: string;
  width?: number;
  height?: number;
}

function columnLetterToZeroBasedIndex(letter: string): number {
  let result = 0;
  for (const char of letter.toUpperCase()) {
    result = result * 26 + (char.charCodeAt(0) - 64);
  }
  return result - 1;
}

function readImageDimensions(rawCell: unknown) {
  if (!rawCell || typeof rawCell !== "object") {
    return { width: undefined, height: undefined };
  }
  const obj = rawCell as { width?: unknown; height?: unknown };
  return {
    width: typeof obj.width === "number" ? obj.width : undefined,
    height: typeof obj.height === "number" ? obj.height : undefined,
  };
}

/** 收集可见行中的图片，按 fileToken 去重 */
export function collectScriptSheetImages(
  rows: ParsedScriptSheetRow[],
  rawValues?: unknown[][]
): ScriptSheetImageRef[] {
  const seen = new Set<string>();
  const items: ScriptSheetImageRef[] = [];

  for (const row of rows) {
    if (!isScriptImageUnderstandingRole(row.role)) continue;

    for (const [column, fileToken] of Object.entries(row.cellImages)) {
      if (!fileToken || seen.has(fileToken)) continue;
      seen.add(fileToken);

      const colIndex = columnLetterToZeroBasedIndex(column);
      const rawCell = rawValues?.[row.rowIndex]?.[colIndex];
      const { width, height } = readImageDimensions(rawCell);

      items.push({
        fileToken,
        rowNumber: row.rowNumber,
        column,
        role: row.role,
        width,
        height,
      });
    }
  }

  return items;
}

export const IMAGE_UNDERSTANDING_CONCURRENCY = 3;
